import { HighlightStyle, syntaxHighlighting } from "@codemirror/language"
import type { Extension } from "@codemirror/state"
import { EditorView } from "@codemirror/view"
import { tags as t } from "@lezer/highlight"
import { alpha, color } from "@repo/fika-design-system/color"
import { fontFamily } from "@repo/fika-design-system/font-family"
import { fontSize } from "@repo/fika-design-system/font-size"

const settings = {
  background: color.sand950,
  foreground: color.base0,
  caret: color.base0,
  selection: alpha(color.rose700, 4),
  lineHighlight: alpha(color.sand800, 5),
}

const styles = [
  { tag: t.comment, color: color.sand600, fontStyle: "italic" },
  { tag: t.keyword, color: color.orange500, fontWeight: 500 },
  { tag: [t.definitionKeyword, t.modifier], color: color.orange500 },
  {
    tag: [t.className, t.tagName, t.definition(t.typeName)],
    color: color.rose500,
  },
  {
    tag: [t.number, t.bool, t.null, t.special(t.brace)],
    color: color.rose500,
  },
  {
    tag: [t.propertyName, t.definition(t.propertyName)],
    color: color.orange200,
  },
  {
    tag: t.function(t.variableName),
    color: color.orange200,
    fontStyle: "italic",
  },
  { tag: t.typeName, color: color.sand600 },
  { tag: [t.variableName], color: color.base0 },
  { tag: t.operator, color: color.indigo300 },
  { tag: t.self, color: color.rose500 },
  { tag: [t.string, t.regexp], color: color.emerald300 },
  { tag: [t.paren, t.bracket], color: color.amber300 },
  { tag: t.labelName, color: color.orange500 },
  { tag: t.attributeName, color: color.rose200 },
  { tag: t.angleBracket, color: color.rose200 },
]

const theme = EditorView.theme(
  {
    "&": {
      color: settings.foreground,
    },
    ".cm-selectionMatch": {
      backgroundColor: alpha(color.amber700, 4),
    },
    ".cm-content": {
      caretColor: settings.caret,
      ...fontSize.m,
      lineHeight: 1.6,
      fontFamily: fontFamily.mono,
    },
    ".cm-cursor, .cm-dropCursor": {
      borderLeftColor: settings.caret,
    },
    "&.cm-focused": {
      outline: "none",
    },
    "&.cm-focused > .cm-scroller > .cm-selectionLayer .cm-selectionBackground, .cm-selectionBackground, .cm-content ::selection":
      {
        backgroundColor: settings.selection,
      },
    ".cm-activeLine": {
      backgroundColor: settings.lineHighlight,
    },
    ".cm-tooltip.cm-tooltip-autocomplete > ul": {
      backgroundColor: settings.background,
    },
    ".cm-tooltip-autocomplete ul li[aria-selected]": {
      backgroundColor: color.indigo700,
      textDecoration: "none",
    },
    ".cm-completionMatchedText": {
      backgroundColor: settings.selection,
    },
  },
  { dark: false },
)

const highlightStyle = HighlightStyle.define(styles)
export const fikaTheme: Extension = [theme, syntaxHighlighting(highlightStyle)]
